import { Link } from 'react-router-dom';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { useSites } from '../../../context/SiteContext';
import { useReportVisits } from '../../../hooks/useReportVisits';
import { buildReferrers, buildSources } from '../../../lib/analytics';
import { formatNumber } from '../../../lib/format';
import { sourceHex } from '../../../lib/source';
import type { SourceChannel } from '../../../lib/types';
import EmptyState from '../../../components/dashboard/EmptyState';
import PageHeader from '../../../components/dashboard/PageHeader';
import StatTable from '../../../components/dashboard/StatTable';
import ExportButton from '../../../components/dashboard/ExportButton';

export default function SourcesPage() {
  const { currentSite } = useSites();
  const { ranged, range } = useReportVisits(currentSite);
  const sources = buildSources(ranged);
  const refs = buildReferrers(ranged);
  const total = sources.reduce((sum, s) => sum + s.value, 0);

  if (!currentSite) {
    return <EmptyState title="先添加一个站点" desc="添加站点后可拆解搜索、外链、社交与直接访问。" />;
  }

  return (
    <div>
      <PageHeader
        title="全部来源"
        desc="看清访客从搜索引擎、社交媒体还是外链进来。"
        range={range}
        extra={
          <ExportButton
            filename={`${currentSite.domain}-sources.csv`}
            columns={['来源类型', '浏览量', '占比']}
            rows={sources.map((s) => [
              s.name,
              formatNumber(s.value),
              total ? `${Math.round((s.value / total) * 100)}%` : '0%',
            ])}
          />
        }
      />

      <div className="mt-6 grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div className="rounded-lg border border-background-200 bg-background-50 p-5">
          <h2 className="text-sm font-semibold text-foreground-900">渠道占比</h2>
          <div className="mt-4 h-72">
            {total === 0 ? (
              <p className="text-sm text-foreground-500">暂无来源数据</p>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={sources} dataKey="value" nameKey="name" innerRadius={60} outerRadius={100}>
                    {sources.map((s) => (
                      <Cell key={s.name} fill={sourceHex(s.name as SourceChannel)} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="rounded-lg border border-background-200 bg-background-50 overflow-hidden">
          <div className="px-5 py-3 border-b border-background-200">
            <h2 className="text-sm font-semibold text-foreground-900">渠道明细</h2>
          </div>
          <StatTable
            columns={['来源类型', '浏览量', '占比']}
            rows={sources.map((s) => [
              s.name,
              formatNumber(s.value),
              total ? `${Math.round((s.value / total) * 100)}%` : '0%',
            ])}
          />
        </div>
      </div>

      <div className="mt-4 rounded-lg border border-background-200 bg-background-50 overflow-hidden">
        <div className="px-5 py-3 border-b border-background-200 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-foreground-900">外部链接 TOP</h2>
          <div className="flex gap-3 text-xs text-primary-600">
            <Link to="/dashboard/referrers">全部外链</Link>
            <Link to="/dashboard/social">社交媒体</Link>
          </div>
        </div>
        <StatTable
          columns={['来源域名', '带来浏览量']}
          rows={refs.slice(0, 20).map((r) => [r.name, formatNumber(r.value)])}
          empty="目前多为直接访问或搜索引擎，还没有可统计的外链来源。"
        />
      </div>
    </div>
  );
}
